import { get, first } from 'lodash';
import { getReviewedPaymentsList, hasExternalPayments } from '../../states/payments/selectors';
import {
	CustomerType,
	PaymentType,
	SME_SEGMENT,
	NextStep,
	ACK_PAGE,
	CustomerJourney,
	CREDIT_CARD,
	OUTCOME_TYPE,
	TO_ACCOUNT,
	FROM_ACCOUNT,
} from '../utils/constants';
import { getUserId } from '../../states/user/selectors';
import { pages } from '../../states/page/actions';
import { CONST_FILE_UPLOAD } from '../../config/constants';

export const getRetrievedOutcome = (state) => get(state, 'effectiveWarning.retrievedOutcome', null);
export const getCurrentWarningPage = (state) => get(state, 'effectiveWarning.currentWarningPage', ACK_PAGE);
export const getEWDetails = (state) => get(state, 'effectiveWarning', null);
export const getSelectedReasonId = (state) => get(state, 'effectiveWarning.reasonId', null);
export const getSelectedReason = (state) => get(state, 'effectiveWarning.reviewData.effectiveWarningReason', null);
export const shouldShowReasonError = (state) => get(state, 'effectiveWarning.reasonError', false);
export const getApiError = (state) => get(state, 'effectiveWarning.apiError', false);
export const isApprovalJourney = ({ effectiveWarning }) => get(effectiveWarning, 'isApprovalJourney', false);
export const isUiError = ({ effectiveWarning }) => get(effectiveWarning, 'uiError', false);
export const getDeclarationValue = ({ effectiveWarning }) => get(effectiveWarning, 'declarationValue', false);
export const getFromAccount = ({ effectiveWarning }) => get(effectiveWarning, 'accountDetails.fromAccount', null);
export const getToAccount = ({ effectiveWarning }) => get(effectiveWarning, 'accountDetails.toAccount', null);
export const getSelectedPayee = ({ effectiveWarning }) => get(effectiveWarning, 'accountDetails.payee', null);
export const getRetrievalId = ({ effectiveWarning }) => get(effectiveWarning, 'retrievalId', null);
export const getEWID = ({ effectiveWarning }) => get(effectiveWarning, 'warningId', null);
export const isBatchPayment = ({ effectiveWarning }) => get(effectiveWarning, 'isBatch', false);
export const getOutcomeType = (state) =>
	get(state, 'effectiveWarning.retrievedOutcome.outcome_type', OUTCOME_TYPE.DYNAMIC);
export const continueApproverJourney = (state) => {
	const nextStep = get(state, 'effectiveWarning.retrievedOutcome.next_step', null);
	return nextStep && nextStep.toUpperCase() === NextStep.PROCEED;
};

export const isTransferOrCCPayment = ({ effectiveWarning }) => {
	const internalTransfer = get(effectiveWarning, 'reviewData.repeatPaymentOptions.isTransfer', false);
	const accType = get(effectiveWarning, 'accountDetails.fromAccount.type', '');
	return internalTransfer || accType === CREDIT_CARD;
};

export const getEWIDs = ({ effectiveWarning }) => {
	const warningIds = get(effectiveWarning, 'ewOutcome.effective_warning.effective_warning_ids', null);
	return warningIds;
};

export const isSMEUser = (state) => {
	const segment = get(state, 'user.segment', 'P');
	return segment.toUpperCase() === SME_SEGMENT;
};

export const getCustomerType = (state) => (isSMEUser(state) ? CustomerType.BUSINESS : CustomerType.RETAIL);

export const getReasons = (state) => {
	const reasons = get(state, 'effectiveWarning.paymentReasons', []);
	return reasons.map((reason) => ({
		id: reason.payment_reason_id,
		header: reason.payment_reason,
		description: reason.additional_details,
	}));
};

export const getNextStep = ({ effectiveWarning }) => {
	const nextStep = get(effectiveWarning, 'ewOutcome.effective_warning.next_step', null);
	return nextStep && nextStep.toUpperCase();
};

export const getPaymentButtonText = (state) => {
	if (!isSMEUser(state)) {
		const isTransfer = get(state, 'effectiveWarning.reviewData.repeatPaymentOptions.isTransfer', false);
		return isTransfer ? 'Make my transfer' : 'Make my payment';
	}
	return 'Continue';
};

const getPaymentDetailsRetail = (state) => {
	const fromAccount = get(getFromAccount(state), 'id', null);
	const toAccount = get(getToAccount(state), 'id', null);
	const payee = get(getSelectedPayee(state), 'id', null);
	const id = getEWID(state);
	return [
		{
			toAccount,
			fromAccount,
			payee,
			id,
		},
	];
};

const getPaymentDetails = (state, payType) => {
	if (!isSMEUser(state)) return getPaymentDetailsRetail(state);

	switch (payType) {
		case PaymentType.FPS:
		case PaymentType.BATCH_FPS:
		case PaymentType.LIST_FPS:
			return get(state, 'payments', null);
		case PaymentType.CHAPS:
		case PaymentType.BATCH_CHAPS:
		case PaymentType.LIST_CHAPS:
			return get(state, 'chaps.payments', null);
		case PaymentType.BACS_CREDIT: {
			const payments = get(state, 'bacsCredit.payments', null);
			const bacsCredit = get(state, 'bacsCredit', null);
			return payments.map((payment) => ({
				...payment,
				id: bacsCredit.id,
				fromAccount: bacsCredit.fromAccount,
				batch_type: bacsCredit.batch_type,
			}));
		}
		default:
			return null;
	}
};

const getPaymentAmount = (state, payType) => {
	if (isSMEUser(state)) {
		return parseFloat(get(first(getPaymentDetails(state, payType)), 'amount', '').replace(/,/g, ''));
	}
	return get(state, 'effectiveWarning.reviewData.repeatData.amount', '');
};

const getFromToAccountDetails = (state, payType = PaymentType.FPS, accountKey) => {
	const fromAccountDetails = (accUuid) => state.accounts.find((account) => account.id === accUuid);
	const toAccountDetails = (accUuid) => state.payees.find((payee) => payee.id === accUuid);

	const accountUUID = get(first(getPaymentDetails(state, payType)), accountKey, null);

	if (accountKey === TO_ACCOUNT) {
		return toAccountDetails(accountUUID);
	}
	return fromAccountDetails(accountUUID);
};

const getAccountDetails = (state, payType) => {
	const SMEUser = isSMEUser(state);
	const toAccountDetails = SMEUser ? getFromToAccountDetails(state, payType, TO_ACCOUNT) : getSelectedPayee(state);
	const fromAccountDetails = SMEUser ? getFromToAccountDetails(state, payType, FROM_ACCOUNT) : getFromAccount(state);
	const accountDetails = {
		toAccount: {
			id: get(toAccountDetails, 'id', null),
			biller_id: get(toAccountDetails, 'biller_id', null),
			sort_code: get(toAccountDetails, 'to_account.sort_code', null),
			account_number: get(toAccountDetails, 'to_account.account_number', null),
			display_name: get(toAccountDetails, 'display_name', '') || get(toAccountDetails, 'to_account.name', ''),
		},
		fromAccount: {
			id: get(fromAccountDetails, 'id', null),
			balances: get(fromAccountDetails, 'balances', []),
			sort_code: get(fromAccountDetails, 'sortCode', null),
			account_number: get(fromAccountDetails, 'accountNumber', null),
			effectiveWarningApplicable: get(fromAccountDetails, 'actionsAvailable./account/payments/effectivewarning', false),
		},
	};
	return accountDetails;
};

export const isStaticWarning = (state, payType) => {
	const paymentsDetatils = getPaymentDetails(state, payType);

	if (paymentsDetatils && paymentsDetatils.length === 1) {
		return paymentsDetatils[0].batch_type === CONST_FILE_UPLOAD.BATCH_TYPE;
	}

	return paymentsDetatils && paymentsDetatils.length > 1;
};

export const getEffectiveWarningId = (state, payType) => {
	const payments = getPaymentDetails(state, payType);
	if (!isStaticWarning(state, payType)) return get(first(payments), 'id', null);
	return getRetrievalId(state);
};

export const getApproverData = (state) => {
	const approverData = get(state, 'effectiveWarning.approverData', {});
	const { verification_id, payments } = approverData;
	const { account_uuid: id, sort_code, account_number } = get(first(payments), 'from_account', {});

	return {
		userId: getUserId(state),
		customerType: getCustomerType(state),
		effectiveWarningId: verification_id,
		accountDetails: {
			fromAccount: {
				id,
				sort_code,
				account_number,
			},
		},
		journey: CustomerJourney.APPROVAL,
		isStatic: false,
		paymentDetails: payments.map(({ from_account }) => ({
			fromAccount: from_account.account_uuid,
			id: verification_id,
		})),
		outcomeType: getOutcomeType(state),
	};
};

export const isTransferOrCCPaymentSME = (state) => {
	const payments = getReviewedPaymentsList(state);
	const isListOrBatch = payments.length > 1;

	if (!isListOrBatch) {
		const isPayingToOtherAccount = !(get(first(payments), 'toAccountDetails.type', '') === 'payee');
		const isPayingFromCreditCard = get(first(payments), 'fromAccountDetails.type', '') === 'credit_card';
		return isPayingToOtherAccount || isPayingFromCreditCard;
	}

	return !hasExternalPayments(state);
};

export const isEffectiveWarningApplicable = (state, payType = PaymentType.FPS) =>
	getAccountDetails(state, payType).fromAccount.effectiveWarningApplicable;

export const getCommonData = (state, payType = PaymentType.FPS) => {
	if (isApprovalJourney(state)) return getApproverData(state);

	return {
		reasonId: getSelectedReasonId(state),
		reason: getSelectedReason(state),
		userId: getUserId(state),
		effectiveWarningId: getEffectiveWarningId(state, payType),
		isSMEUser: isSMEUser(state),
		customerType: getCustomerType(state),
		journey: CustomerJourney.CREATE,
		retrievalId: getRetrievalId(state),
		paymentDetails: getPaymentDetails(state, payType),
		accountDetails: getAccountDetails(state, payType),
		paymentAmount: getPaymentAmount(state, payType),
		isStatic: isStaticWarning(state, payType),
		outcomeType: isStaticWarning(state, payType) ? OUTCOME_TYPE.STATIC : OUTCOME_TYPE.DYNAMIC,
	};
};

export const getNextPage = (state) => {
	const effectiveWarningNextStep = getNextStep(state);
	switch (effectiveWarningNextStep) {
		case NextStep.ACKNOWLEDGEMENT:
			return pages.EffectiveWarning.warning;
		case NextStep.PROCEED:
			return null;
		default:
			return NextStep.CONTINUE_TO_PAYMENT;
	}
};
